import { convertEmptyToUndefined } from "@core/domain/types/undefinable.type";
import { CostIVATypeEnumDto } from "@entity/data/dto/cost-iva-type.dto";
import { CreateCostBody } from "@entity/data/dto/cost/create-cost.body";
import { CostIVATypeEnumMapper } from "@entity/data/mappers/cost/cost-iva-type-enum.mapper";
import { ServiceProvidedEnumMapper } from "@entity/data/mappers/supplier/service-provided-enum.mapper";
import { CreateCost } from "@entity/domain/models/cost/create-cost.model";
import { CostFormValuesValidated } from "@entity/presentation/component/cost-form/cost-form.component";
import { inject, injectable } from "inversify";
import { IdentificationEnumMapper } from "../deposit/identification-enum.mapper";
import { CostTypeEnumMapper } from "./cost-type-enum.mapper";
import { IRPFTypeEnumMapper } from "./irpf-type-enum.mapper";
import { PaymentMethodTypeEnumMapper } from "./payment-method-type.mapper";

@injectable()
export class CreateCostMapper {
    constructor(
        @inject(PaymentMethodTypeEnumMapper)
        private readonly paymentMethodTypeEnumMapper: PaymentMethodTypeEnumMapper,
        @inject(IdentificationEnumMapper)
        private readonly identificationEnumMapper: IdentificationEnumMapper,
        @inject(CostTypeEnumMapper)
        private readonly costTypeEnumMapper: CostTypeEnumMapper,
        @inject(CostIVATypeEnumMapper)
        private readonly costIvaTypeEnumMapper: CostIVATypeEnumMapper,
        @inject(IRPFTypeEnumMapper)
        private readonly irpfTypeEnumMapper: IRPFTypeEnumMapper,
        @inject(ServiceProvidedEnumMapper)
        private serviceProvidedEnumMapper: ServiceProvidedEnumMapper,
    ) {}
    mapFromCostFormValues(
        costFormValuesValidated: CostFormValuesValidated,
        entityId: number,
    ): CreateCost {
        return new CreateCost(
            entityId,
            costFormValuesValidated.invoiceNum,
            costFormValuesValidated.concept,
            costFormValuesValidated.socialReason,
            costFormValuesValidated.identificationType,
            costFormValuesValidated.identification,
            costFormValuesValidated.costType,
            costFormValuesValidated.invoiceDate,
            costFormValuesValidated.paymentDate,
            costFormValuesValidated.paymentMethod,
            costFormValuesValidated.base,
            costFormValuesValidated.iva,
            costFormValuesValidated.otherIva ?? 0,
            0,
            costFormValuesValidated.totalCost,
            costFormValuesValidated.irpfType,
            costFormValuesValidated.otherCostType ?? undefined,
            convertEmptyToUndefined(costFormValuesValidated.otherPaymentMethod),
            undefined,
            costFormValuesValidated.additionalExpensesToUpdate,
            convertEmptyToUndefined(costFormValuesValidated.category),
            convertEmptyToUndefined(costFormValuesValidated.otherCategory),
        );
    }

    mapToDto(createCost: CreateCost): CreateCostBody {
        const identificationType = this.identificationEnumMapper.mapToDto(
            createCost.issuerIdentificationType,
        );

        const paymentMethod = this.paymentMethodTypeEnumMapper.mapToDto(
            createCost.paymentMethod,
        );

        const type = this.costTypeEnumMapper.mapToDto(createCost.type);

        const iva = this.costIvaTypeEnumMapper.mapToDto(createCost.iva);

        const irpf = createCost.irpfType
            ? this.irpfTypeEnumMapper.mapToDto(createCost.irpfType)
            : "";

        const categories = createCost.category
            ? createCost.category.map((cat) =>
                  this.serviceProvidedEnumMapper.mapToDto(cat),
              )
            : undefined;

        return {
            number: createCost.invoiceNum,
            concept: createCost.concept,
            issuer_name: createCost.issuerName,
            issuer_identification_type: identificationType,
            issuer_identification_number: createCost.identificationNumber,
            type: type,
            others_type: createCost.othersType ?? "",
            issue_date: convertEmptyToUndefined(
                createCost.invoiceDate?.toISO(),
            ),
            payment_date: convertEmptyToUndefined(
                createCost.paymentDate?.toISO(),
            ),
            payment_method: convertEmptyToUndefined(paymentMethod),
            other_payment_method: createCost.otherPaymentMethod ?? "",
            base: createCost.base ?? 0,
            iva: iva ?? CostIVATypeEnumDto.Cero,
            iva_other: convertEmptyToUndefined(createCost.otherIva),
            total: convertEmptyToUndefined(createCost.total),
            // discount: createCost.discount ?? 0,
            irpf_type: irpf,
            entity: createCost.entityId,
            document: createCost.document?.id,
            categories,
            other_category: convertEmptyToUndefined(createCost.otherCategory),
        };
    }
}
